import {
  DatePicker,
  DateTimePicker,
  TimePicker,
} from "@mui/x-date-pickers-pro";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import { PickerTextField } from "./PickerTextField.tsx";
import * as styles from "./DatePickerV2.styles.ts";
import React, { forwardRef } from "react";
import type {
  DatePickerProps,
  DateTimePickerProps,
  PickerValidDate,
  TimePickerProps,
} from "@mui/x-date-pickers-pro";
import { BaseInputV2Adornment } from "src/components/BaseInputV2/BaseInputV2Adornment.tsx";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

type CommonProps = {
  mode?: "date" | "dateTime" | "time";
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  floatingError?: boolean;
};

type Props = CommonProps &
  (CommonProps["mode"] extends "date"
    ? Omit<DatePickerProps<PickerValidDate>, "label">
    : Omit<DateTimePickerProps<PickerValidDate>, "label">);

const muiTheme = createTheme();

const ClearIcon = forwardRef(({ ownerState, ...restProps }: any, ref: any) => {
  return <i ref={ref} {...restProps} className="icon-15-clear_x" />;
});
const ClearButton = forwardRef(({ ownerState, ...restProps }: any, ref) => {
  return <BaseInputV2Adornment ref={ref} isAction={true} {...restProps} />;
});

const OpenPickerButton = ({ ownerState, ...restProps }: any, ref: any) => {
  return <BaseInputV2Adornment isAction={true} {...restProps} />;
};

const OpenPickerIcon = forwardRef((props: any, ref: any) => (
  <i ref={ref} className="icon-15-calendar" />
));

const DatePickerV2 = (props: Props) => {
  const {
    mode = "date",
    label,
    error,
    helperText,
    floatingError = false,
    className,
    required,
    disabled,
    ...restProps
  } = props;

  return (
    <div css={styles.root} className={className}>
      {label && (
        <InputLabel label={label} required={required} helperText={helperText} />
      )}

      <MuiThemeProvider theme={muiTheme}>
        {mode === "date" ? (
          <DatePicker
            {...(restProps as DatePickerProps<PickerValidDate>)}
            slots={{
              clearIcon: ClearIcon,
              clearButton: ClearButton,
              textField: PickerTextField,
              openPickerButton: OpenPickerButton,
              openPickerIcon: OpenPickerIcon,
            }}
            disabled={disabled}
            css={styles.datePicker}
            slotProps={{
              inputAdornment: { position: "start" },
              field: {
                clearable: true,
                // @ts-ignore
                mode,
                customError: error,
                floatingError,
              },
            }}
          />
        ) : mode === "time" ? (
          <TimePicker
            {...(restProps as TimePickerProps<PickerValidDate>)}
            slots={{
              clearIcon: ClearIcon,
              clearButton: ClearButton,
              textField: PickerTextField,
              openPickerButton: OpenPickerButton,
              openPickerIcon: OpenPickerIcon,
            }}
            disabled={disabled}
            css={styles.datePicker}
            slotProps={{
              inputAdornment: { position: "start" },
              field: {
                clearable: true,
                // @ts-ignore
                mode,
                customError: error,
                floatingError,
              },
            }}
          />
        ) : (
          <DateTimePicker
            {...(restProps as DateTimePickerProps<PickerValidDate>)}
            slots={{
              clearIcon: ClearIcon,
              clearButton: ClearButton,
              textField: PickerTextField,
              openPickerButton: OpenPickerButton,
              openPickerIcon: OpenPickerIcon,
            }}
            disabled={disabled}
            css={styles.datePicker}
            slotProps={{
              inputAdornment: { position: "start" },
              field: {
                clearable: true,
                // @ts-ignore
                mode,
                customError: error,
                floatingError,
              },
            }}
          />
        )}
      </MuiThemeProvider>
    </div>
  );
};

DatePickerV2.displayName = "DatePickerV2";

export { DatePickerV2 };
